import React, { useMemo, useState } from "react";
import { useCurrency } from "../../utils/useCurrency";
import { useTableSort } from "../../utils/useTableSort";
import { Loader } from "../Loader";
import styles from "./TablaCanonica.module.scss";

// TablaCanonica — molde único de tabla del proyecto (regla 10.161). Fusiona lo mejor de las 3
// candidatas del relevamiento (docs/decisiones/2026-09-29-molde-tabla-canonica-fase1-relevamiento.md):
// aspecto visual de MovimientosBancarios (header oscuro sticky, bordes redondeados, hover,
// columnas de plata con tabular-nums), orden por columna con el hook canónico useTableSort (como
// VentasFacturas), columna de acciones + anchos estables con colgroup (como EdificiosTable), fila
// expandible (como GestionActivos). Ficha completa con ejemplos: docs/componentes/tabla-canonica.md.
//
// Toda tabla NUEVA del proyecto usa este componente — prohibido armar una tabla suelta (10.161).

const formatFechaDefault = (valor) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  const d =
    valor instanceof Date
      ? valor
      : new Date(typeof valor === "string" && valor.length === 10 ? `${valor}T12:00:00` : valor);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const ALIGN_POR_TIPO = { plata: "right", fecha: "center", acciones: "center", texto: "left", custom: "left" };
const SORT_TYPE_POR_TIPO = { plata: "number", fecha: "date", texto: "text", custom: "text" };

/**
 * @param {Array<{
 *   key: string,                          // clave de columna — también la key de sort y de `valores` en filasTotales
 *   label: React.ReactNode,                // texto del header
 *   tipo?: 'texto'|'plata'|'fecha'|'acciones'|'custom',  // default 'texto'
 *   accessor?: (fila) => any,              // default fila[key] — SIEMPRE el valor crudo/comparable, nunca formateado
 *   render?: (valor, fila) => React.ReactNode,  // override total de la celda (obligatorio para tipo 'acciones')
 *   moneda?: string,                       // tipo 'plata', default 'CLP' (useCurrency.formatAmount)
 *   colorPorSigno?: boolean,                // tipo 'plata', default true — verde/rojo por signo, nunca +/− (10.161)
 *   ancho?: string,                         // ej. '12%' — activa <colgroup> + table-layout:fixed
 *   align?: 'left'|'center'|'right',       // default por tipo
 *   ordenable?: boolean,                    // default true (false para 'acciones')
 *   sortType?: 'text'|'text-numeric'|'number'|'date',  // default por tipo
 *   sortAccessor?: (fila) => any,          // si el valor de orden difiere del accessor de display
 *   nulls?: 'first'|'last',                 // default 'last'
 * }>} columnas
 * @param {Array<object>} filas
 * @param {(fila:object, index:number) => string|number} [keyExtractor]  default fila.id ?? index
 * @param {boolean} [cargando]              10.273 — mientras es true, nunca se muestra "vacío"
 * @param {React.ReactNode} [vacio]         mensaje cuando `filas` está vacío y `cargando` es false
 * @param {boolean} [ordenable]             gate general del ordenamiento por header, default true
 * @param {(fila:object) => React.ReactNode|null|undefined} [filaExpandible]  si una fila devuelve
 *   contenido, esa fila tiene flecha de expandir; si devuelve null/undefined, esa fila no la tiene
 * @param {Array<{label:React.ReactNode, valores:Record<string,any>}>} [filasTotales]  filas de <tfoot>
 * @param {'normal'|'compacta'} [densidad]
 * @param {boolean} [stickyHeader]          default true
 */
const TablaCanonica = ({
  columnas,
  filas = [],
  keyExtractor,
  cargando = false,
  vacio = "Sin resultados",
  ordenable = true,
  filaExpandible = null,
  filasTotales = null,
  densidad = "normal",
  stickyHeader = true,
  className = "",
}) => {
  const { formatAmount } = useCurrency();
  const [expandidas, setExpandidas] = useState(() => new Set());

  const resolverValor = (col, fila) => (col.accessor ? col.accessor(fila) : fila[col.key]);

  const formatearValor = (col, valor) => {
    if (col.tipo === "plata") return formatAmount(valor, col.moneda || "CLP");
    if (col.tipo === "fecha") return formatFechaDefault(valor);
    return valor === null || valor === undefined || valor === "" ? "—" : valor;
  };

  const claseMonto = (col, valorCrudo) => {
    if (col.tipo !== "plata" || col.colorPorSigno === false) return "";
    const n = Number(valorCrudo);
    if (!Number.isFinite(n) || n === 0) return "";
    return n > 0 ? styles.montoPositivo : styles.montoNegativo;
  };

  // columnDefs para useTableSort (10.203) — derivados de `columnas`, nunca reimplementados.
  const columnDefs = useMemo(() => {
    const defs = {};
    for (const col of columnas) {
      if (col.tipo === "acciones" || col.ordenable === false) continue;
      defs[col.key] = {
        accessor: (fila) => (col.sortAccessor ? col.sortAccessor(fila) : resolverValor(col, fila)),
        type: col.sortType || SORT_TYPE_POR_TIPO[col.tipo] || "text",
        nulls: col.nulls || "last",
      };
    }
    return defs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnas]);

  const { sortConfig, handleSort, sortIndicator, sortRows } = useTableSort(columnDefs);
  const filasOrdenadas = ordenable ? sortRows(filas) : filas;

  const getKey = (fila, index) => (keyExtractor ? keyExtractor(fila, index) : (fila.id ?? index));

  const toggleExpandida = (key) => {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const hayExpandible = typeof filaExpandible === "function";
  const usaColgroup = columnas.some((c) => c.ancho);
  const totalColsSpan = columnas.length + (hayExpandible ? 1 : 0);

  const wrapperClass = [styles.tableResponsive, className].filter(Boolean).join(" ");
  const tablaClass = [styles.table, densidad === "compacta" ? styles.compacta : "", stickyHeader ? styles.sticky : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClass}>
      <table className={tablaClass}>
        {usaColgroup && (
          <colgroup>
            {hayExpandible && <col style={{ width: "2.5rem" }} />}
            {columnas.map((col) => (
              <col key={col.key} style={col.ancho ? { width: col.ancho } : undefined} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {hayExpandible && <th aria-hidden="true" />}
            {columnas.map((col) => {
              const esOrdenable = ordenable && col.tipo !== "acciones" && col.ordenable !== false;
              const align = col.align || ALIGN_POR_TIPO[col.tipo] || "left";
              if (!esOrdenable) {
                return (
                  <th key={col.key} style={{ textAlign: align }}>
                    {col.label}
                  </th>
                );
              }
              const dir = sortConfig.key === col.key ? sortConfig.dir : null;
              const ariaSort = dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none";
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={styles.sortable}
                  style={{ textAlign: align }}
                  onClick={() => handleSort(col.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSort(col.key);
                    }
                  }}
                  tabIndex={0}
                  aria-sort={ariaSort}
                >
                  {col.label}
                  {sortIndicator(col.key)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {cargando && (
            <tr>
              <td colSpan={totalColsSpan} className={styles.estado}>
                <div style={{ maxHeight: 140, overflow: "hidden" }}>
                  <Loader />
                </div>
              </td>
            </tr>
          )}
          {!cargando && filasOrdenadas.length === 0 && (
            <tr>
              <td colSpan={totalColsSpan} className={styles.estado}>
                {vacio}
              </td>
            </tr>
          )}
          {!cargando &&
            filasOrdenadas.map((fila, index) => {
              const key = getKey(fila, index);
              const contenidoExpandido = hayExpandible ? filaExpandible(fila) : null;
              const puedeExpandir = hayExpandible && contenidoExpandido != null;
              const abierta = puedeExpandir && expandidas.has(key);
              return (
                <React.Fragment key={key}>
                  <tr>
                    {hayExpandible && (
                      <td className={styles.expandCell}>
                        {puedeExpandir && (
                          <button
                            type="button"
                            className={styles.btnExpandir}
                            onClick={() => toggleExpandida(key)}
                            aria-label={abierta ? "Contraer" : "Expandir"}
                            aria-expanded={abierta}
                          >
                            {abierta ? "▾" : "▸"}
                          </button>
                        )}
                      </td>
                    )}
                    {columnas.map((col) => {
                      const align = col.align || ALIGN_POR_TIPO[col.tipo] || "left";
                      const valor = resolverValor(col, fila);
                      const contenido = col.render ? col.render(valor, fila) : formatearValor(col, valor);
                      const claseCelda = col.tipo === "plata" ? `${styles.celdaMonto} ${claseMonto(col, valor)}` : "";
                      return (
                        <td key={col.key} className={claseCelda} style={{ textAlign: align }}>
                          {contenido}
                        </td>
                      );
                    })}
                  </tr>
                  {abierta && (
                    <tr className={styles.filaDetalle}>
                      <td colSpan={totalColsSpan}>{contenidoExpandido}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
        {filasTotales && filasTotales.length > 0 && (
          <tfoot>
            {filasTotales.map((ft, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>
                {hayExpandible && <td />}
                {columnas.map((col, ci) => {
                  const align = col.align || ALIGN_POR_TIPO[col.tipo] || "left";
                  if (ci === 0) {
                    return (
                      <td key={col.key} style={{ textAlign: align }}>
                        {ft.label}
                      </td>
                    );
                  }
                  const valor = ft.valores ? ft.valores[col.key] : undefined;
                  if (valor === undefined) return <td key={col.key} />;
                  const claseCelda = col.tipo === "plata" ? `${styles.celdaMonto} ${claseMonto(col, valor)}` : "";
                  return (
                    <td key={col.key} className={claseCelda} style={{ textAlign: align }}>
                      {formatearValor(col, valor)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default TablaCanonica;
